import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';

type ButtonVariant = 'primary' | 'gold' | 'outline' | 'outline-light' | 'ghost';
type ButtonSize = 'default' | 'lg' | 'sm';

const variantClass: Record<ButtonVariant, string> = {
  primary: 'b b-primary',
  gold: 'b b-gold',
  outline: 'b b-outline',
  'outline-light': 'b b-outline-light',
  ghost: 'b b-ghost',
};

const sizeClass: Record<ButtonSize, string> = {
  default: '',
  lg: 'b-lg',
  sm: 'b-sm',
};

const MotionLink = motion.create(Link);

interface RedesignButtonProps {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
  to?: string;
  href?: string;
  onClick?: () => void;
  external?: boolean;
}

const tapSpring = { type: 'spring' as const, stiffness: 420, damping: 28 };

export function RedesignButton({
  children,
  variant = 'primary',
  size = 'default',
  className,
  to,
  href,
  onClick,
  external,
}: RedesignButtonProps) {
  const reduce = useReducedMotion();
  const classes = cn(variantClass[variant], sizeClass[size], className);
  const motionProps = reduce
    ? {}
    : { whileHover: { y: -2 }, whileTap: { scale: 0.97 }, transition: tapSpring };

  if (to) {
    return (
      <MotionLink to={to} className={classes} onClick={onClick} {...motionProps}>
        {children}
      </MotionLink>
    );
  }

  if (href) {
    return (
      <motion.a
        href={href}
        className={classes}
        target={external ? '_blank' : undefined}
        rel={external ? 'noopener noreferrer' : undefined}
        {...motionProps}
      >
        {children}
      </motion.a>
    );
  }

  return (
    <motion.button type="button" className={classes} onClick={onClick} {...motionProps}>
      {children}
    </motion.button>
  );
}
